import { Fragment } from "react";
import { euro, fmtDate } from "@/lib/format";

// Groups quote line items by category, preserving first-appearance order.
function groupByCategory(voci) {
  const groups = [];
  const idx = {};
  (voci || []).forEach((v) => {
    const cat = v.categoria || "Generale";
    if (!(cat in idx)) {
      idx[cat] = groups.length;
      groups.push({ categoria: cat, items: [] });
    }
    groups[idx[cat]].items.push(v);
  });
  return groups;
}

const lineTotal = (v) => v.quantita * v.prezzo * (1 - (v.sconto || 0) / 100);

// Printable Italian construction quote. Rendered inside #print-area for PDF export.
export default function QuotePDF({ quote, settings, contact, tipo = "PREVENTIVO", domId = "print-area" }) {
  if (!quote) return null;
  const accent = settings?.accent_color || "#E1352A";
  const logo = settings?.logo_url;
  const groups = groupByCategory(quote.voci);

  return (
    <div id={domId} data-no-translate className="bg-white text-slate-900 mx-auto" style={{ maxWidth: 820, padding: 40 }}>
      {/* Header: company text on the left, logo + document title on the right */}
      <div className="flex justify-between items-start gap-6 pb-5 mb-6" style={{ borderBottom: `3px solid ${accent}` }}>
        <div>
          <div className="font-heading text-xl font-extrabold uppercase tracking-tight" style={{ color: accent }}>
            {settings?.ragione_sociale || ""}
          </div>
          <div className="text-xs text-slate-500 mt-2 leading-relaxed">
            {settings?.indirizzo && <div>{settings.indirizzo}</div>}
            {settings?.piva && <div>P.IVA {settings.piva}</div>}
            {settings?.telefono && <div>Tel. {settings.telefono}</div>}
            {settings?.email && <div>{settings.email}</div>}
          </div>
        </div>
        <div className="text-right shrink-0">
          {logo && (
            <img src={logo} alt="logo" crossOrigin="anonymous" style={{ maxHeight: 58, maxWidth: 180, marginLeft: "auto", marginBottom: 10, objectFit: "contain" }} />
          )}
          <div className="font-heading text-3xl font-extrabold leading-none" style={{ color: accent }}>{tipo}</div>
          <div className="text-sm font-semibold mt-1">N. {quote.numero || "—"}</div>
          <div className="text-xs text-slate-500 mt-2">Data: {fmtDate(quote.data_emissione)}</div>
          {quote.scadenza && tipo !== "FATTURA" && <div className="text-xs text-slate-500">Validità fino al: {fmtDate(quote.scadenza)}</div>}
          {tipo === "FATTURA" && quote.quote_numero && <div className="text-xs text-slate-500">Rif. preventivo N. {quote.quote_numero}</div>}
        </div>
      </div>

      {/* Client */}
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Spett.le</div>
        <div className="font-semibold text-lg">{quote.cliente_nome || contact?.ragione_sociale || "—"}</div>
        {contact && (
          <div className="text-xs text-slate-500 mt-1 leading-relaxed">
            {contact.referente && <div>Att.ne {contact.referente}</div>}
            {contact.indirizzo && <div>{contact.indirizzo}</div>}
            {contact.piva && <div>P.IVA {contact.piva}</div>}
          </div>
        )}
      </div>

      {/* Oggetto */}
      {quote.oggetto && (
        <div className="mb-5 text-sm">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Oggetto: </span>
          <span className="font-medium">{quote.oggetto}</span>
        </div>
      )}

      {/* Categorized items */}
      <table className="w-full text-xs mb-4" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: accent, color: "#fff" }}>
            <th className="text-left py-2 px-2 font-semibold" style={{ width: 60 }}>Cod.</th>
            <th className="text-left py-2 px-2 font-semibold">Designazione lavori</th>
            <th className="text-right py-2 px-2 font-semibold" style={{ width: 55 }}>Q.tà</th>
            <th className="text-center py-2 px-2 font-semibold" style={{ width: 55 }}>U.M.</th>
            <th className="text-right py-2 px-2 font-semibold" style={{ width: 80 }}>P. Unit.</th>
            <th className="text-right py-2 px-2 font-semibold" style={{ width: 90 }}>Totale €</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g, gi) => {
            const sub = g.items.reduce((s, v) => s + lineTotal(v), 0);
            return (
              <Fragment key={gi}>
                <tr style={{ background: "#f1f5f9" }}>
                  <td colSpan={6} className="py-1.5 px-2 font-bold uppercase text-[11px] tracking-wide text-slate-700">
                    {g.categoria}
                  </td>
                </tr>
                {g.items.map((v, i) => (
                  <tr key={`${gi}-${i}`} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td className="py-1.5 px-2 text-slate-500">{v.codice || "—"}</td>
                    <td className="py-1.5 px-2">{v.nome}{v.sconto ? ` (sconto ${v.sconto}%)` : ""}</td>
                    <td className="py-1.5 px-2 text-right">{v.quantita}</td>
                    <td className="py-1.5 px-2 text-center">{v.unita}</td>
                    <td className="py-1.5 px-2 text-right">{euro(v.prezzo)}</td>
                    <td className="py-1.5 px-2 text-right font-medium">{euro(lineTotal(v))}</td>
                  </tr>
                ))}
                <tr key={`s-${gi}`} style={{ borderBottom: "1px solid #cbd5e1" }}>
                  <td colSpan={5} className="py-1.5 px-2 text-right font-semibold text-slate-600 italic">
                    Totale {g.categoria}
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold">{euro(sub)}</td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mb-6">
        <div className="w-72 text-sm space-y-1.5">
          {quote.sconto_importo > 0 && (
            <>
              <div className="flex justify-between">
                <span className="text-slate-500">Totale lavori</span>
                <span className="font-medium">{euro((quote.imponibile || 0) + quote.sconto_importo)}</span>
              </div>
              <div className="flex justify-between" data-testid="pdf-discount-row">
                <span className="text-slate-500">Sconto{quote.sconto_tipo !== "importo" && quote.sconto_valore ? ` (${quote.sconto_valore}%)` : ""}</span>
                <span className="font-medium">− {euro(quote.sconto_importo)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">Totale imponibile (IVA esclusa)</span>
            <span className="font-medium">{euro(quote.imponibile)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">IVA</span>
            <span className="font-medium">{euro(quote.iva_totale)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 mt-1" style={{ borderTop: `2px solid ${accent}`, color: accent }}>
            <span>TOTALE</span>
            <span>{euro(quote.totale)}</span>
          </div>
        </div>
      </div>

      {/* Terms & notes */}
      <div className="text-xs text-slate-600 space-y-2.5 leading-relaxed">
        <div>
          <span className="font-semibold text-slate-800">Modalità di pagamento: </span>
          {quote.termini_pagamento || settings?.termini_default}
        </div>
        {settings?.iban && (
          <div><span className="font-semibold text-slate-800">IBAN: </span>{settings.iban}</div>
        )}
        {(quote.note || settings?.nota_default) && (
          <div><span className="font-semibold text-slate-800">Nota: </span>{quote.note || settings?.nota_default}</div>
        )}
        {(quote.garanzia || settings?.garanzia_default) && (
          <div data-testid="pdf-garanzia"><span className="font-semibold text-slate-800">Garanzia: </span>{quote.garanzia || settings?.garanzia_default}</div>
        )}
      </div>

      {settings?.slogan && (
        <div className="text-center font-heading font-bold text-sm mt-8 uppercase tracking-widest" style={{ color: accent }}>
          {settings.slogan}
        </div>
      )}

      <div className="flex justify-between pt-10 text-xs text-slate-600">
        <div className="border-t border-slate-300 w-48 pt-1">Timbro e firma azienda</div>
        <div className="border-t border-slate-300 w-48 pt-1 text-right">Firma per accettazione</div>
      </div>

      <div className="text-center text-[10px] text-slate-400 mt-8">{settings?.ragione_sociale ? `Gestionale di ${settings.ragione_sociale}` : "K Gestionale"}</div>
    </div>
  );
}
